import {useState, useEffect} from 'react';
import {useForm} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import {GridMappingSchema, GridFormValues} from '../gridSchema';
import {
  IIngestionCopyResponse,
  IMappingResponse,
} from '../../../../api/types/ingestionApi.type';

export const useIngestionMappingGrid = (
  loadedData: IIngestionCopyResponse | null,
  dataLoaded: boolean
) => {
  const [mappingData, setMappingData] = useState<IMappingResponse | null>(null);

  const gridForm = useForm<GridFormValues>({
    resolver: zodResolver(GridMappingSchema),
    defaultValues: {
      columnMappings: [],
    },
    mode: 'onChange',
  });

  // Set mapping data from loaded data
  useEffect(() => {
    if (!loadedData || !dataLoaded) return;

    if (loadedData.ingestionWizardModel.ingestionMappingFields) {
      setMappingData({
        sourceField:
          loadedData.ingestionWizardModel.ingestionMappingFields.sourceField,
        type: loadedData.ingestionWizardModel.ingestionMappingFields.type,
        rowData: loadedData.ingestionWizardModel.ingestionMappingFields.rowData,
        rowCount:
          loadedData.ingestionWizardModel.ingestionMappingFields.rowCount,
        sourceList:
          loadedData.ingestionWizardModel.ingestionMappingFields.sourceList,
      });
    }
  }, [loadedData, dataLoaded]);

  // Prefill grid with mapping data
  useEffect(() => {
    if (!loadedData || !mappingData) return;

    const mappings = loadedData.ingestionWizardModel.ingestionMapping;
    if (!mappings || mappings.length === 0) return;

    const formattedMappings = mappings.map((row) => {
      const sourceFieldOption = mappingData.sourceField.find(
        (sf) => String(sf.value) === String(row.selectedSourceFieldID)
      );
      const typeOption = mappingData.type.find(
        (t) => t.value === row.selectedTypeID
      );
      const destinationOption = typeOption?.destinationField.find(
        (d) => d.value === row.selectedDestinationID
      );

      return {
        sourceField: sourceFieldOption
          ? {value: sourceFieldOption.value, text: sourceFieldOption.text}
          : null,
        type: typeOption
          ? {value: typeOption.value, text: typeOption.text}
          : null,
        destinationField: destinationOption
          ? {value: destinationOption.value, text: destinationOption.text}
          : null,
        mandatory: row.mandatory,
        isNewRow: row.isNewRow,
        clientUniqueIdentifierRow: row.clientUniqueIdentifierRow,
      };
    });

    gridForm.setValue('columnMappings', formattedMappings);
  }, [loadedData, mappingData, gridForm]);

  return {
    gridForm,
    mappingData,
    setMappingData,
  };
};
